import { Injectable } from "@angular/core";
import { DecisionAction, DecisionActionRequest, DecisionActionResult, DecisionDetail, DecisionDetailItem, DecisionListItem, NextBestDecision } from "../../models/home";
import { LanguageService } from "../language/language.service";
import { HomeDecisionActionPresentationBuilder } from "./home-decision-action-presentation.builder";
import { HomeDecisionActionRequestBuilder } from "./home-decision-action-request.builder";
import {
  DecisionActionCatalogOwner,
  DecisionActionFeedback,
  DecisionActionLike,
  DecisionActionPresentation
} from "./home-decision-action.types";

export type {
  DecisionActionFeedback,
  DecisionActionLike,
  DecisionActionPresentation,
  DecisionActionPrimaryMode,
  DecisionActionStatusTone,
  DecisionUrgencyTone
} from "./home-decision-action.types";

@Injectable({ providedIn: "root" })
export class HomeDecisionActionUxService {
  constructor(
    private readonly lang: LanguageService,
    private readonly presentationBuilder: HomeDecisionActionPresentationBuilder,
    private readonly requestBuilder: HomeDecisionActionRequestBuilder
  ) {}

  getHeroActionPresentation(nextDecision: NextBestDecision | null): DecisionActionPresentation {
    return this.presentationBuilder.getHeroActionPresentation(nextDecision);
  }

  getDetailActionPresentation(detail: DecisionDetail | null, selectedActionKey?: string | null): DecisionActionPresentation {
    void selectedActionKey;
    return this.presentationBuilder.getDetailActionPresentation(detail);
  }

  getListActionLabel(item: DecisionListItem): string | null {
    return this.presentationBuilder.getListActionLabel(item);
  }

  getPrimaryAction(decision: DecisionActionCatalogOwner | null): DecisionActionLike | null {
    return this.presentationBuilder.getPrimaryAction(decision);
  }

  getPrimaryActionForNextDecision(nextDecision: NextBestDecision | null): DecisionActionLike | null {
    return this.presentationBuilder.getPrimaryActionForNextDecision(nextDecision);
  }

  getOperationalActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    return this.presentationBuilder.getOperationalActions(decision);
  }

  getSecondaryActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    return this.presentationBuilder.getSecondaryActions(decision);
  }

  getRemediationActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    return this.presentationBuilder.getRemediationActions(decision);
  }

  getDangerActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    return this.presentationBuilder.getDangerActions(decision);
  }

  getDisabledActions(decision: DecisionDetailItem | null): readonly DecisionAction[] {
    return this.presentationBuilder.getDisabledActions(decision);
  }

  requiresForm(action: DecisionActionLike | null): boolean {
    return this.presentationBuilder.requiresForm(action);
  }

  canExecuteHeroAction(action: DecisionActionLike | null): boolean {
    return this.presentationBuilder.canExecuteHeroAction(action);
  }

  buildRequest(action: DecisionActionLike | null, formValue?: Record<string, unknown> | null): DecisionActionRequest | null {
    return this.requestBuilder.buildRequest(action, formValue);
  }

  getActionResultMessage(result: DecisionActionResult): DecisionActionFeedback {
    if (result.message) {
      return {
        tone: this.resolveResultTone(result),
        message: result.message
      };
    }

    switch (result.actionResultType) {
      case "state_changed":
        if (result.outcome === "manual_business_completion") {
          return { tone: "success", message: this.lang.translate("home.decisionDetail.actionResult.manualBusinessCompletion") };
        }

        if (result.outcome === "business_action_acknowledged") {
          return { tone: "info", message: this.lang.translate("home.decisionDetail.actionResult.businessAcknowledged") };
        }

        return { tone: "success", message: this.lang.translate("home.decisionDetail.actionResult.stateChanged") };
      case "reevaluation_queued":
        return { tone: "info", message: this.lang.translate("home.decisionDetail.actionResult.reevaluationQueued") };
      case "inspection_only":
        return { tone: "info", message: this.lang.translate("home.decisionDetail.actionResult.inspectionOnly") };
      case "rejected":
        return { tone: "error", message: this.lang.translate("home.decisionDetail.actionResult.rejected") };
      case "replay":
        return { tone: "info", message: this.lang.translate("home.decisionDetail.actionResult.replay") };
      default:
        if (result.success && !result.isConflict) {
          return { tone: "success", message: this.lang.translate("home.decisionDetail.actionSuccess") };
        }

        return { tone: "error", message: this.lang.translate("home.decisionDetail.actionFailedCopy") };
    }
  }

  shouldShowReevaluationPending(result: DecisionActionResult): boolean {
    return result.actionResultType === "reevaluation_queued" || result.reevaluationStatus === "pending";
  }

  private resolveResultTone(result: DecisionActionResult): "success" | "info" | "error" {
    if (result.isConflict || !result.success || result.actionResultType === "rejected") {
      return "error";
    }

    if (
      result.actionResultType === "inspection_only" ||
      result.actionResultType === "reevaluation_queued" ||
      result.actionResultType === "replay" ||
      result.outcome === "business_action_acknowledged"
    ) {
      return "info";
    }

    return "success";
  }
}
